import React, { useEffect, useState } from 'react';
import { dayjs } from 'shared/utils/date';
import { useQuery } from 'hooks/apollo';
import { FormattedMessage } from 'react-intl';
import { gql } from '@apollo/client';

import { calculateDateRanges } from 'shared/constants/date';

import {
  Form,
  Collapse,
  Checkbox,
  Select,
  Tag,
  Empty,
  Spin,
  Button,
} from 'antd';
import { DownOutlined } from '@ant-design/icons';
import ErrorBoundary from 'components/ErrorBoundary';
import LanguageSelect from 'components/LanguageSelect';
import CountrySelect from 'components/CountrySelect';
import resultsMessages from 'components/QuestionResults/messages'; // Move these to a global place
import FormLabel from 'components/FormLabel';
import globalMessages from 'translations/messages';
import theme from 'shared/constants/theme.json';

import messages from '../messages';
import './segment-form.less';

const GET_ALL_FILTERS = gql`
  query SegmentForm_getAllFilters($surveyGroupId: ID!, $surveyId: ID!, $customerId: ID!){
    questionFilters(surveyGroupId: $surveyGroupId, surveyId: $surveyId) {
      id
      relationId
      title
      type
      options {
        id
        label
        startDate
        endDate
      }
    }
    taggedfieldsFilters(surveyGroupId: $surveyGroupId, surveyId: $surveyId) {
      id
      relationId
      title
      type
      options { id label }
    }
    surveyFilters(surveyGroupId: $surveyGroupId) {
      id
      relationId
      title
      type
      options { id label }
    }
    respondentFilters(surveyGroupId: $surveyGroupId, customerId: $customerId) {
      id
      relationId
      title
      type
      options { id label }
    }
    websiteFilters(surveyGroupId: $surveyGroupId, customerId: $customerId) {
      id
      relationId
      title
      type
      options { id label }
    }
  }
`;

const GET_REGIONS = gql`
  query SegmentForm_GetRegions($ids: [ID!]) {
    regions(ids: $ids) {
      id
      title
    }
  }
`;

const GET_MUNICIPALITIES = gql`
  query SegmentForm_GetMunicipalities($ids: [ID!]) {
    municipalities(ids: $ids) {
      id
      title
    }
  }
`;

const { Panel } = Collapse;

const RegionSelect = ({ ids, ...props }) => {
  const { Option } = Select;
  const { data: { regions = [] } = {} } = useQuery(GET_REGIONS, {
    fetchPolicy: 'cache-first',
    variables: {
      ids,
    },
  });

  return (
    <Select
      {...props}
      mode="multiple"
      optionFilterProp="children"
    >
      {regions.map(c => <Option key={c.id}>{c.title}</Option>)}
    </Select>
  );
};

const MunicipalitySelect = ({ ids, ...props }) => {
  const { Option } = Select;
  const { data: { municipalities = [] } = {} } = useQuery(GET_MUNICIPALITIES, {
    fetchPolicy: 'cache-first',
    variables: {
      ids,
    },
  });

  return (
    <Select
      {...props}
      mode="multiple"
      optionFilterProp="children"
    >
      {municipalities.map(c => <Option key={c.id}>{c.title}</Option>)}
    </Select>
  );
};

const FilterFormItem = ({ filterItem, formPath, dateRanges, initialValue, isEditable }) => {
  const { Option } = Select;

  let control;

  let mode = 'multiple';

  switch (filterItem.type) {
    case 'COUNTRY':
      control = <CountrySelect countryCodes={filterItem.options.map(o => o.id)} mode="multiple" disabled={!isEditable} />;
      break;
    case 'LANGUAGE':
      control = <LanguageSelect include={filterItem.options.map(o => o.id)} mode="multiple" disabled={!isEditable} />;
      break;
    case 'REGION':
      control = <RegionSelect ids={filterItem.options.map(o => o.id)} disabled={!isEditable} />;
      break;
    case 'MUNICIPALITY':
      control = <MunicipalitySelect ids={filterItem.options.map(o => o.id)} disabled={!isEditable} />;
      break;
    case 'TEXT':
      mode = 'tags';
      break;
    default:
  }

  if (!control) {
    const options = filterItem.options.map(o => {
      let label = o.id;

      if (resultsMessages[o.id]) {
        label = <FormattedMessage {...resultsMessages[o.id]} />;
      } else if (o.label) {
        label = <span dangerouslySetInnerHTML={{ __html: o.label }} />;
      }

      if (o.startDate !== undefined || o.endDate !== undefined) {
        const hasResponsesWithinDateRanges = o.startDate && o.endDate && dateRanges.some(dr => !dr.startDate.isAfter(o.endDate) && !dr.endDate.isBefore(o.startDate));

        if (!hasResponsesWithinDateRanges) {
          label = (
            <>
              {label}
              *
            </>
          );
        }
      }

      return {
        ...o,
        label,
      };
    });
    if (options.length > 11 || mode === 'tags') {
      control = <Select mode={mode} optionFilterProp="children" disabled={!isEditable}>{options.map(o => <Option key={o.id}>{o.label}</Option>)}</Select>;
    } else {
      control = <Checkbox.Group options={options.map(o => ({ value: o.id, label: o.label }))} disabled={!isEditable} />;
    }
  }

  const label = messages[`${filterItem.title}Label`] || <span dangerouslySetInnerHTML={{ __html: filterItem.title }} />;

  const description = messages[`${mode}Description`];

  return (
    <Form.Item label={<FormLabel label={label} description={description} />} name={[...formPath, filterItem.relationId]} initialValue={initialValue}>
      {control}
    </Form.Item>
  );
};

const getFilterPanel = ({
  filters,
  filterOptions,
  showSelectedOptions,
  section,
  dateRanges,
  isEditable,
  formPath,
  setShowSelectedOptions,
}) => {
  const selectedFilters = filters.filter(f => filterOptions.find(fo => f.relationId === fo.relationId));
  const filtersToShow = showSelectedOptions && selectedFilters.length > 0 ? selectedFilters : filters;
  const hiddenFilters = filters.length - filtersToShow.length;
  const panelInfo = `${selectedFilters.length} / ${filters.length}`;

  return (
    <Panel
      key={section}
      forceRender
      header={<FormattedMessage {...messages[section.toLowerCase()]} />}
      extra={<Tag color={selectedFilters.length > 0 && theme['primary-color']}>{panelInfo}</Tag>}
    >
      {section === 'QUESTION' && <p><small><FormattedMessage {...messages.noResponsesWithinDateRange} /></small></p>}
      <ErrorBoundary>
        {filters.length === 0 && (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={<FormattedMessage {...messages.noFilters} />}
          />
        )}
        {filtersToShow.length > 0 && filtersToShow.map(filterItem => {
          const filterOption = filterOptions.find(fo => filterItem.relationId === fo.relationId);
          return <FilterFormItem key={filterItem.id} filterItem={filterItem} formPath={[...formPath, section]} dateRanges={dateRanges} initialValue={filterOption?.options} isEditable={isEditable && filterOption?.isEditable !== false} />;
        })}
        {hiddenFilters > 0 && (
          <Button block type="link" onClick={() => setShowSelectedOptions(false)}>
            {hiddenFilters}
            {' '}
            hidden filters, show all
            <DownOutlined />
          </Button>
        )}
      </ErrorBoundary>
    </Panel>
  );
};

const SegmentFiltersForm = ({
  customerId,
  surveyGroupId,
  displayedSurveyId,
  dateRange,
  filterOptions = [],
  isEditable,
  formPath = [],
  onFilterLoaded,
}) => {
  const [showSelectedOptions, setShowSelectedOptions] = useState(filterOptions.length > 0);

  const { loading, updating, data: filterData } = useQuery(GET_ALL_FILTERS, {
    variables: {
      surveyGroupId,
      surveyId: displayedSurveyId,
      customerId,
    },
    skip: !surveyGroupId,
  });

  useEffect(() => {
    if (filterData && onFilterLoaded) {
      onFilterLoaded();
    }
  }, [filterData, loading, updating, onFilterLoaded]);

  const dateRanges = calculateDateRanges(dayjs, dateRange);

  const sections = ['SURVEY', 'QUESTION', 'TAGGEDFIELDS', 'RESPONDENT', 'WEBSITE'];
  const defaultActiveKey = [];

  const renderPanels = () => sections.map((section) => {
    const sectionFilterOptions = filterOptions.filter(fo => fo.type === section);

    if (showSelectedOptions && sectionFilterOptions.length) {
      defaultActiveKey.push(section);
    }

    const filters = filterData?.[`${section.toLowerCase()}Filters`] ?? [];

    return getFilterPanel({
      section,
      filters,
      filterOptions: sectionFilterOptions,
      showSelectedOptions,
      dateRanges,
      isEditable,
      formPath,
      setShowSelectedOptions,
    });
  });

  return (
    <div className="segment-filters-form">
      <h3>
        <FormattedMessage {...messages.filterCategories} />
      </h3>
      <Spin size="large" spinning={!filterData && loading} tip={<FormattedMessage {...globalMessages.loading} />}>
        <Collapse defaultActiveKey={defaultActiveKey}>
          {renderPanels()}
        </Collapse>
      </Spin>
    </div>
  );
};

export default SegmentFiltersForm;

const mapSelectedFilters = (existingFilterOptions, filters, type) => Object.keys(filters)
  .filter(x => filters[x] && filters[x].length)
  .map((relationId) => {
    const filterOption = existingFilterOptions.find(fo => fo.relationId === relationId) || {};

    return {
      id: filterOption.id,
      relationId,
      options: filters[relationId],
      type,
    };
  });

export function toFilterOptionsInput(existingFilterOptions, selectedFilters) {
  return Object.keys(selectedFilters)
    .map(section => mapSelectedFilters(existingFilterOptions, selectedFilters[section], section))
    .flat();
}
